import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../services/api';
import './PatientDashboard.css';
import { appName } from '../brand';

function PatientDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [sortBy, setSortBy] = useState('name');
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Delete mode: only offered to users allowed to delete (admins, or users an
  // admin granted "can delete records"). Off by default so trash icons don't
  // clutter the cards or invite accidental clicks.
  const [canDelete, setCanDelete] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Create patient state
  const [showCreatePatient, setShowCreatePatient] = useState(false);
  const [creatingPatient, setCreatingPatient] = useState(false);
  const [newPatientData, setNewPatientData] = useState({
    name: '',
    age: '',
    care_agent: ''
  });

  const loadPatients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Always use the canonical individuals endpoint
      const patientsData = await apiService.getIndividuals();

      console.log('Loaded individuals:', patientsData);
      
      // Transform the data to match the expected format
      const transformedPatients = patientsData.map(patient => ({
        id: patient.id,
        name: patient.name,
        age: patient.age,
        // Prefer updated_at (real edit timestamp) and fall back to last_visit
        // for older rows that pre-date the field being exposed by the API.
        lastUpdated: patient.updated_at || patient.last_visit,
        careAgent: patient.care_agent,
        status: patient.status,
        completionRate: patient.completion_rate,
      }));
      
      setPatients(transformedPatients);
    } catch (err) {
      console.error('Failed to load individuals:', err);
      setError('Failed to load individuals. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    loadPatients();
  }, [loadPatients]);

  // The delete permission comes from the signed-in account (server-enforced too).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const auth = await apiService.authCheck();
        if (!cancelled && (auth?.can_delete_records || auth?.is_admin)) setCanDelete(true);
      } catch (_) { /* not authed; leave canDelete false */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const filteredPatients = patients
    .filter(patient => {
      const matchesSearch = patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           patient.careAgent.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === 'All' || patient.status === filterStatus;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'lastUpdated':
          return new Date(b.lastUpdated) - new Date(a.lastUpdated);
        case 'completion':
          return b.completionRate - a.completionRate;
        default:
          return 0;
      }
    });

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'var(--primary-green)';
      case 'Follow-up Required': return 'var(--primary-yellow)';
      case 'Inactive': return 'var(--text-light)';
      default: return 'var(--text-medium)';
    }
  };

  const handleDeletePatient = (patient, e) => {
    e.stopPropagation(); // Prevent navigation to patient details
    setPatientToDelete(patient);
    setShowDeleteConfirm(true);
  };

  const confirmDeletePatient = async () => {
    if (!patientToDelete) return;
    
    setDeleting(true);
    try {
      await apiService.deleteIndividual(patientToDelete.id);
      
      // Remove patient from local state
      setPatients(prev => prev.filter(p => p.id !== patientToDelete.id));
      
      // Reset delete state
      setShowDeleteConfirm(false);
      setPatientToDelete(null);
      
      console.log(`Individual ${patientToDelete.name} deleted successfully`);
    } catch (err) {
      console.error('Failed to delete individual:', err);
      alert(err?.message || 'Failed to delete individual. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setPatientToDelete(null);
  };

  // Create patient functions
  const handleCreatePatient = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!newPatientData.name.trim() || !newPatientData.age) {
      alert('Please fill in all required fields (Name and Age)');
      return;
    }
    
    setCreatingPatient(true);
    try {
      const createdPatient = await apiService.createIndividual({
        name: newPatientData.name.trim(),
        age: parseInt(newPatientData.age),
        care_agent: newPatientData.care_agent.trim() || 'System'
      });
      
      // Add the new patient to the local state
      setPatients(prev => [...prev, {
        id: createdPatient.id,
        name: createdPatient.name,
        age: createdPatient.age,
        lastUpdated: createdPatient.updated_at || createdPatient.last_visit,
        careAgent: createdPatient.care_agent,
        status: createdPatient.status,
        completionRate: createdPatient.completion_rate,
      }]);
      
      // Reset form and close modal
      setNewPatientData({ name: '', age: '', care_agent: '' });
      setShowCreatePatient(false);
      
      // Show success message and navigate to the new patient
      alert(`Individual ${createdPatient.name} created successfully! You will be redirected to their profile.`);
      navigate(`/individual/${createdPatient.id}`);

    } catch (err) {
      console.error('Failed to create individual:', err);
      alert('Failed to create individual. Please try again.');
    } finally {
      setCreatingPatient(false);
    }
  };

  const updateNewPatientField = (field, value) => {
    setNewPatientData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const cancelCreatePatient = () => {
    setShowCreatePatient(false);
    setNewPatientData({ name: '', age: '', care_agent: '' });
  };

  return (
    <div className="patient-dashboard">
      {/* Header */}
      <header className="app-header">
        <div className="header-content">
          <div className="logo-section">
            <div className="logo">{appName()}</div>
          </div>
          <nav className="nav-menu">
            <button className="nav-item" onClick={() => navigate('/')}>Dashboard</button>
            <button className="nav-item active">Support Plans</button>
            <button className="nav-item" onClick={() => navigate('/import')}>Import</button>
            <button
              className="nav-item"
              onClick={async () => {
                try { await apiService.logout(); } catch (_) {}
                window.location.reload();
              }}
              title="Sign out"
            >
              Sign out
            </button>
          </nav>
        </div>
      </header>

      <div className="dashboard-content">
        {/* Page Header */}
        <div className="page-header">
          <div className="page-title-section">
            <h1>Support Plan Platform</h1>
            <p>Manage comprehensive support plans for all individuals</p>
          </div>
          <div className="header-actions">
            <button 
              className="add-patient-btn primary"
              onClick={() => navigate('/import')}
            >
              <span>🤖</span> Import Support Plan
            </button>
            <button 
              className="add-patient-btn secondary"
              onClick={() => setShowCreatePatient(true)}
            >
              <span>+</span> Add Manually
            </button>
            {canDelete && (
              <button
                className={`add-patient-btn delete-mode-toggle ${deleteMode ? 'on' : ''}`}
                onClick={() => setDeleteMode(m => !m)}
                aria-pressed={deleteMode}
                title={deleteMode ? 'Hide the delete buttons' : 'Show a delete button on each card'}
              >
                <span>🗑️</span> {deleteMode ? 'Done deleting' : 'Delete records'}
              </button>
            )}
          </div>
        </div>

        {deleteMode && (
          <div className="delete-mode-banner">
            <strong>Delete mode is on.</strong> Click the 🗑️ on a card to permanently delete that
            individual and their support plan. You'll be asked to confirm first.
            <button onClick={() => setDeleteMode(false)}>Turn off</button>
          </div>
        )}

        {/* Search and Filters */}
        <div className="search-filters">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search individuals or support people..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <span className="search-icon">🔍</span>
          </div>
          
          <div className="filters">
            <select 
              value={filterStatus} 
              onChange={(e) => setFilterStatus(e.target.value)}
              className="filter-select"
            >
              <option value="All">All Status</option>
              <option value="Active">Active</option>
              <option value="Follow-up Required">Follow-up Required</option>
              <option value="Inactive">Inactive</option>
            </select>
            
            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              className="filter-select"
            >
              <option value="name">Sort by Name</option>
              <option value="lastUpdated">Sort by Last Updated</option>
              <option value="completion">Sort by Completion</option>
            </select>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading plans...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="error-container">
            <div className="error-message">
              <p>{error}</p>
              <button onClick={loadPatients} className="retry-btn">Retry</button>
            </div>
          </div>
        )}

        {/* Individual Grid */}
        {!loading && !error && (
          <div className="patients-grid">
            {filteredPatients.map(patient => (
              <div
                key={patient.id}
                className={`patient-card ${deleteMode ? 'delete-mode' : ''}`}
                onClick={() => navigate(`/individual/${patient.id}`)}
              >
                <div className="patient-header">
                  <div className="patient-info">
                    <h3>{patient.name}</h3>
                    <p className="patient-age">Age {patient.age}</p>
                  </div>
                  <div className="header-actions">
                    {deleteMode && (
                      <button
                        className="delete-patient-btn"
                        onClick={(e) => handleDeletePatient(patient, e)}
                        title={`Delete ${patient.name}`}
                        aria-label={`Delete ${patient.name}`}
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="patient-details">
                  <div className="detail-row">
                    <span className="label">Support Person:</span>
                    <span className="value">{patient.careAgent}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Last Updated:</span>
                    <span className="value">{patient.lastUpdated ? new Date(patient.lastUpdated).toLocaleDateString() : '—'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Status:</span>
                    <span 
                      className="value status"
                      style={{ color: getStatusColor(patient.status) }}
                    >
                      {patient.status}
                    </span>
                  </div>
                </div>
                
                <div className="completion-section">
                  <div className="completion-label">
                    <span>Plan Completion</span>
                    <span className="completion-percentage">{patient.completionRate}%</span>
                  </div>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ width: `${patient.completionRate}%` }}
                    ></div>
                  </div>
                </div>
                
                <div className="card-footer">
                  <span className="view-plan">View Support Plan →</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && !error && filteredPatients.length === 0 && (
          <div className="no-results">
            <div className="no-results-icon">🔍</div>
            <h3>No individuals found</h3>
            <p>Try adjusting your search terms or filters</p>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && patientToDelete && (
        <div className="modal-overlay">
          <div className="modal-content delete-confirmation">
            <h3>⚠️ Confirm Individual Deletion</h3>
            <div className="warning-content">
              <p>You are about to permanently delete:</p>
              <div className="patient-to-delete">
                <strong>{patientToDelete.name}</strong> (Age {patientToDelete.age})
              </div>
              <p className="warning-text">
                This action is <strong>permanent</strong> and will delete:
              </p>
              <ul>
                <li>All individual information</li>
                <li>Complete support plan data</li>
                <li>All associated records</li>
              </ul>
              <p className="final-warning">
                <strong>This cannot be undone!</strong>
              </p>
            </div>
            <div className="modal-actions">
              <button 
                type="button" 
                onClick={cancelDelete}
                disabled={deleting}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="danger" 
                onClick={confirmDeletePatient}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Yes, Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Individual Modal */}
      {showCreatePatient && (
        <div className="modal-overlay" onClick={cancelCreatePatient}>
          <div className="modal-content create-patient-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Create New Individual Profile</h3>
            <form onSubmit={handleCreatePatient}>
              <div className="form-group">
                <label htmlFor="patient-name">Individual Name *</label>
                <input
                  id="patient-name"
                  type="text"
                  value={newPatientData.name}
                  onChange={(e) => updateNewPatientField('name', e.target.value)}
                  placeholder="Enter individual's full name"
                  required
                  autoFocus
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="patient-age">Age *</label>
                <input
                  id="patient-age"
                  type="number"
                  value={newPatientData.age}
                  onChange={(e) => updateNewPatientField('age', e.target.value)}
                  placeholder="Enter individual's age"
                  min="1"
                  max="120"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="care-agent">Support Person</label>
                <input
                  id="care-agent"
                  type="text"
                  value={newPatientData.care_agent}
                  onChange={(e) => updateNewPatientField('care_agent', e.target.value)}
                  placeholder="Enter support person name (optional)"
                />
              </div>
              
              <div className="form-info">
                <p><strong>Note:</strong> A new individual profile will be created with basic information. You can add detailed support plan information after creation.</p>
              </div>
              
              <div className="modal-actions">
                <button 
                  type="button" 
                  onClick={cancelCreatePatient}
                  disabled={creatingPatient}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="primary"
                  disabled={creatingPatient}
                >
                  {creatingPatient ? 'Creating...' : 'Create Individual'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default PatientDashboard; 